import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, Building2 } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";

const PROFESIONES = [
  { v: "psicologia", l: "Psicología" },
  { v: "medicina", l: "Medicina" },
  { v: "odontologia", l: "Odontología" },
  { v: "fisioterapia", l: "Fisioterapia / Kinesiología" },
  { v: "nutricion", l: "Nutrición" },
  { v: "abogacia", l: "Abogacía" },
  { v: "contaduria", l: "Contaduría" },
  { v: "arquitectura", l: "Arquitectura" },
  { v: "escribania", l: "Escribanía" },
  { v: "otra", l: "Otra profesión independiente" },
];

const TITULOS = ["Lic.", "Dra.", "Dr.", "Arq.", "Ing.", "Cr.", "Cra.", "Escrib.", "Ab."];

export default function Ajustes() {
  const cfg = useConfig();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(cfg || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.profesion, cfg.profesional_nombre]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.refresh;
      delete payload.id;
      delete payload.created_at;
      if (payload.sesiones_totales) payload.sesiones_totales = Number(payload.sesiones_totales);
      await api.put("/config", payload);
      await cfg.refresh();
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1000px]">
      <PageHeader
        eyebrow="Configuración"
        title="Ajustes del consultorio"
        description="Personalizá los textos y datos del sistema según tu profesión. Los cambios se aplican de inmediato."
        action={
          <button data-testid="ajustes-save" onClick={save} disabled={loading} className="btn-primary-pill">
            <Save className="w-4 h-4 mr-1.5" />
            {loading ? "Guardando…" : "Guardar cambios"}
          </button>
        }
      />

      <div className="space-y-6">
        <Section title="Profesión y terminología" icon={Building2}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tipo de profesión">
              <select
                data-testid="ajustes-profesion"
                className="input-clinic"
                value={form.profesion || "psicologia"}
                onChange={(e) => set("profesion", e.target.value)}
              >
                {PROFESIONES.map((p) => (
                  <option key={p.v} value={p.v}>
                    {p.l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Título del profesional">
              <select
                data-testid="ajustes-titulo"
                className="input-clinic"
                value={form.profesional_titulo || "Lic."}
                onChange={(e) => set("profesional_titulo", e.target.value)}
              >
                {TITULOS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Nombre del profesional (o del titular)">
              <input
                data-testid="ajustes-nombre-prof"
                className="input-clinic"
                value={form.profesional_nombre || ""}
                onChange={(e) => set("profesional_nombre", e.target.value)}
              />
            </Field>
            <Field label="Nombre del consultorio / estudio">
              <input
                data-testid="ajustes-consultorio"
                className="input-clinic"
                value={form.consultorio_nombre || ""}
                onChange={(e) => set("consultorio_nombre", e.target.value)}
              />
            </Field>
            <Field label='Cómo llamás a tus clientes (singular, ej: "paciente")'>
              <input
                data-testid="ajustes-termino-sing"
                className="input-clinic"
                value={form.termino_paciente_singular || ""}
                onChange={(e) => set("termino_paciente_singular", e.target.value)}
              />
            </Field>
            <Field label='Cómo los llamás (plural, ej: "pacientes")'>
              <input
                data-testid="ajustes-termino-plur"
                className="input-clinic"
                value={form.termino_paciente_plural || ""}
                onChange={(e) => set("termino_paciente_plural", e.target.value)}
              />
            </Field>
            <Field label='Nombre del encuentro (ej: "sesión", "consulta", "audiencia")'>
              <input
                data-testid="ajustes-termino-sesion"
                className="input-clinic"
                value={form.termino_sesion || ""}
                onChange={(e) => set("termino_sesion", e.target.value)}
              />
            </Field>
            <Field label="Cantidad de encuentros por tratamiento (0 = ilimitado)">
              <input
                data-testid="ajustes-sesiones"
                type="number"
                min="0"
                className="input-clinic"
                value={form.sesiones_totales ?? 8}
                onChange={(e) => set("sesiones_totales", e.target.value)}
              />
            </Field>
            <Field label='Nombre del test o evaluación inicial (dejá vacío si no aplica)'>
              <input
                data-testid="ajustes-test"
                className="input-clinic"
                value={form.test_inicial_nombre || ""}
                onChange={(e) => set("test_inicial_nombre", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section title="Consultorio y horarios">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Dirección del consultorio">
              <input
                data-testid="ajustes-direccion"
                className="input-clinic"
                placeholder="Ej: Avda. Mariscal López 1234, Asunción"
                value={form.consultorio_direccion || ""}
                onChange={(e) => set("consultorio_direccion", e.target.value)}
              />
            </Field>
            <Field label="Link Google Maps (opcional)">
              <input
                data-testid="ajustes-gmaps"
                className="input-clinic"
                placeholder="https://maps.google.com/..."
                value={form.consultorio_gmaps_url || ""}
                onChange={(e) => set("consultorio_gmaps_url", e.target.value)}
              />
            </Field>
            <Field label="Horarios de atención (texto libre)">
              <input
                data-testid="ajustes-horarios"
                className="input-clinic"
                value={form.horarios_texto || ""}
                onChange={(e) => set("horarios_texto", e.target.value)}
              />
            </Field>
            <Field label="Código de país para WhatsApp (Paraguay = 595)">
              <input
                data-testid="ajustes-codpais"
                className="input-clinic"
                value={form.codigo_pais_whatsapp || "595"}
                onChange={(e) => set("codigo_pais_whatsapp", e.target.value)}
              />
            </Field>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card-clinic p-6 md:p-8">
      <div className="flex items-center gap-2 mb-5">
        {Icon && <Icon className="w-4 h-4 text-neutral-500" />}
        <h3 className="text-lg font-bold tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
